import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import API_PATH from "~/utils/api-routes";
import { createSmkApi } from "~/utils/smkInterseptor";

export interface FcmNotificationPreferences {
  daily_darshan: 0 | 1;
  daily_katha: 0 | 1;
  new_releases: 0 | 1;
  live_stream: 0 | 1;
  all_other: 0 | 1;
  daily_live_quiz: 0 | 1;
  nirnay: 0 | 1;
  device_type?: string;
  device_browser?: string;
  os_type?: string;
  os_browser?: string;
  ip_address?: string;
  country_code?: string;
  country?: string;
}

export interface FcmTokenData extends FcmNotificationPreferences {
  fcm_token: string;
}

export interface UpdateOldTokenPayload {
  old_fcm_token: string;
  new_fcm_token: string;
}

export interface UpdateNotificationPreferencesPayload extends FcmNotificationPreferences {
  fcm_token: string;
}

export interface DeleteFcmTokenPayload {
  fcm_token: string;
}

export interface GetFcmTokenPayload {
  fcm_token: string;
}

export interface FcmTokenResponse extends FcmNotificationPreferences {
  fcm_token: string;
  id?: number;
  created_at?: string;
  updated_at?: string;
}

interface SettingState {
  fcmToken: string | null;
  notificationPreferences: FcmNotificationPreferences | null;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: SettingState = {
  fcmToken: null,
  notificationPreferences: null,
  loading: false,
  error: null,
  success: false,
};

const smkService = createSmkApi();

// 0. Get FCM token data
export const getFcmTokenThunk = createAsyncThunk("setting/getFcmToken", async (payload: GetFcmTokenPayload, { rejectWithValue }) => {
  try {
    const response = await smkService.get(API_PATH.FCM_TOKEN_GET, { params: payload });
    return response.data.data as FcmTokenResponse;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "Failed to get FCM token data";
    return rejectWithValue(message);
  }
});

// 1. Insert new FCM token
export const insertFcmTokenThunk = createAsyncThunk("setting/insertFcmToken", async (tokenData: FcmTokenData, { rejectWithValue }) => {
  try {
    const response = await smkService.post(API_PATH.FCM_TOKEN_INSERT, tokenData);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "Failed to insert FCM token";
    return rejectWithValue(message);
  }
});

// 2. Update FCM old token
export const updateOldFcmTokenThunk = createAsyncThunk("setting/updateOldFcmToken", async (payload: UpdateOldTokenPayload, { rejectWithValue }) => {
  try {
    const response = await smkService.put(API_PATH.FCM_TOKEN_UPDATE_OLD, payload);
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "Failed to update FCM token";
    return rejectWithValue(message);
  }
});

// 3. Update FCM notification preferences
export const updateNotificationPreferencesThunk = createAsyncThunk(
  "setting/updateNotificationPreferences",
  async (payload: UpdateNotificationPreferencesPayload, { rejectWithValue }) => {
    try {
      const response = await smkService.put(API_PATH.FCM_TOKEN_UPDATE_PREFERENCES, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data.message || "Failed to update notification preferences";
      return rejectWithValue(message);
    }
  },
);

// 4. Delete FCM token
export const deleteFcmTokenThunk = createAsyncThunk("setting/deleteFcmToken", async (payload: DeleteFcmTokenPayload, { rejectWithValue }) => {
  try {
    const response = await smkService.delete(API_PATH.FCM_TOKEN_DELETE, { data: payload });
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "Failed to delete FCM token";
    return rejectWithValue(message);
  }
});

const settingSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    setFcmToken: (state, action) => {
      state.fcmToken = action.payload;
    },
    setNotificationPreferences: (state, action) => {
      state.notificationPreferences = action.payload;
    },
    updateNotificationPreference: (state, action: PayloadAction<{ field: keyof FcmNotificationPreferences; value: 0 | 1 }>) => {
      const { field, value } = action.payload;
      if (state.notificationPreferences) {
        (state.notificationPreferences[field] as 0 | 1) = value;
      }
    },
    clearError: (state) => {
      state.error = null;
      state.success = false;
    },
    resetSettingState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Get FCM token
    builder
      .addCase(getFcmTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getFcmTokenThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.fcmToken = action.payload.fcm_token;
        state.notificationPreferences = {
          daily_darshan: action.payload.daily_darshan,
          daily_katha: action.payload.daily_katha,
          new_releases: action.payload.new_releases,
          live_stream: action.payload.live_stream,
          all_other: action.payload.all_other,
          daily_live_quiz: action.payload.daily_live_quiz,
          nirnay: action.payload.nirnay,
        };
        state.success = true;
        state.error = null;
      })
      .addCase(getFcmTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    // Insert FCM token
    builder
      .addCase(insertFcmTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(insertFcmTokenThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.notificationPreferences = {
          daily_darshan: action.payload.daily_darshan,
          daily_katha: action.payload.daily_katha,
          new_releases: action.payload.new_releases,
          live_stream: action.payload.live_stream,
          all_other: action.payload.all_other,
          daily_live_quiz: action.payload.daily_live_quiz,
          nirnay: action.payload.nirnay,
        };
        state.error = null;
      })
      .addCase(insertFcmTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    // Update old FCM token
    builder
      .addCase(updateOldFcmTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOldFcmTokenThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateOldFcmTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    // Update notification preferences
    builder
      .addCase(updateNotificationPreferencesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateNotificationPreferencesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferencesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    // Delete FCM token
    builder
      .addCase(deleteFcmTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteFcmTokenThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.fcmToken = null;
        state.notificationPreferences = null;
      })
      .addCase(deleteFcmTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const { setFcmToken, setNotificationPreferences, clearError, resetSettingState, updateNotificationPreference } = settingSlice.actions;

export default settingSlice.reducer;
